import type { SignatureField } from '@/features/admin/documents/hooks/useSignatureRequests';
import type { OnboardingDocumentKind } from '@/features/onboarding/lib/onboardingDocuments';

export type DocumentType = 'Fiche de paie' | 'Contrat' | 'Autre';

export enum DocumentKind {
  LIBRARY = 'LIBRARY',
  TO_SIGN = 'TO_SIGN',
  RECEIPT = 'RECEIPT',
}

export enum SignStatus {
  PENDING = 'PENDING',
  SIGNED = 'SIGNED',
  REJECTED = 'REJECTED',
}

export enum ReceiptStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export type DocumentItem = {
  id: string | number;
  name: string;
  type: DocumentType;
  url: string;
  /**
   * La date à laquelle la pièce **est entrée au coffre** — jamais la période qu'elle couvre
   * (suivora-api#1251).
   */
  dateAdded: string;
  /**
   * La période couverte par la pièce — `null` quand elle n'en couvre aucune.
   *
   * Un contrat, un document généré : ils existent à une date, ils ne couvrent pas un mois. `null`
   * est donc un **état normal**, pas un manque, et l'écran retombe alors sur `dateAdded`.
   *
   * Ni `0` ni une date fabriquée : l'API a nettoyé les deux, parce que zéro prétend savoir et
   * range un bulletin dans l'an zéro.
   */
  periodYear?: number | null;
  /** Le mois couvert, pour l'ordre à l'intérieur d'une année. `null` avec `periodYear`. */
  periodMonth?: number | null;
  kind?: DocumentKind;
  signTitle?: string;
  signStatus?: SignStatus;
  /**
   * **Où ce document se signe** (#2535, suivora-api#1897) — figé au dépôt. `INTEGRATED` : dans
   * l'application (`POST /documents/:id/sign`) ; `YOUSIGN` : chez le prestataire. **Absent** : une
   * API sans signature intégrée — aucun geste n'est proposé, plutôt qu'un bouton qui ne signe rien.
   */
  signMechanism?: 'INTEGRATED' | 'YOUSIGN';
  /** Les cases où il signe, tracées par l'expéditeur — mises en évidence sur l'aperçu. */
  signatureFields?: SignatureField[];
  receiptStatus?: ReceiptStatus;
  /** Nom du fichier d'origine (justificatifs d'onboarding). */
  fileName?: string;
  /** Motif de refus fourni par la RH (justificatif RECEIPT au statut REJECTED). */
  rejectedReason?: string;
  /** URL (signée) d'une vignette d'aperçu du document — affichée dans la carte du coffre-fort. */
  thumbnailUrl?: string;
  /** Type de pièce d'onboarding (justificatif RECEIPT) — cible du re-dépôt `POST …/documents/:kind`. */
  docKind?: OnboardingDocumentKind;
  /** Date de décision RH (validation ou refus) d'un justificatif ; absente tant qu'en attente. */
  reviewedAt?: string;
};

export const DOCUMENTS_TYPE: DocumentType[] = ['Fiche de paie', 'Contrat', 'Autre'];

export type GroupedByYear = Record<number, DocumentItem[]>;
